import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, Link, useLocation } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaCloudUploadAlt } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import AuthBackgroundSeller from '../../components/Home/AuthBackground/AuthBackgroundSeller';
import SellerTermsModal from '../../components/TermsModal/SellerTermsModal';
import img from "../../assets/users/logo.png";
import { toast } from 'react-toastify';

// Reusable input component
const CommonInput = ({
  type = "text",
  name,
  value,
  placeholder,
  required = false,
  disabled = false,
  onChange,
  error = false
}) => {
  return (
    <div className="mb-4 w-full">
      <input
        type={type}
        name={name}
        value={value}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        onChange={onChange}
        className={`border-brand-primary w-full rounded-md border p-2 
          ${disabled ? "bg-gray-300 cursor-not-allowed" : "bg-gray-100"}
          ${error ? "border-red-500" : ""}`}
      />
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

function SellerRegistration() {
  const { SellerRegister, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [apiErrors, setApiErrors] = useState({});
  const [verifiedEmail, setVerifiedEmail] = useState("");

  // Initialize form data with email from location state or local storage
  useEffect(() => {
    const emailFromState = location.state?.email;
    const emailFromStorage = localStorage.getItem('sellerVerificationEmail');

    if (emailFromState) {
      setVerifiedEmail(emailFromState);
      localStorage.setItem('sellerVerificationEmail', emailFromState);
    } else if (emailFromStorage) {
      setVerifiedEmail(emailFromStorage);
    }
  }, [location.state]);

  // Seller registration state
  const [formData, setFormData] = useState({
    first_name: "",
    email: verifiedEmail,
    mobile_number: "",
    password: "",
    confirm_password: "",
    company: {
      company_name: "",  // Changed from 'name'
      company_mail: "",   // Changed from 'mail_id'
      location: "",
      logo: null,
    },
  });

  // Update personal email when verifiedEmail changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      email: verifiedEmail
    }));
  }, [verifiedEmail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const names = name.split('.');

    if (names.length === 1) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      const [parent, child] = names;
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    }

    // Clear errors dynamically
    setFieldErrors((prev) => ({ ...prev, [name]: false }));
    setApiErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Handle file upload for company logo.
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      setFieldErrors((prev) => ({ ...prev, "company.logo": false }));
    }
    setFormData((prev) => ({
      ...prev,
      company: { ...prev.company, logo: file },
    }));
  };

  // Validate all required fields on the first page (personal information)
  const handleNextStep = () => {
    const newFieldErrors = {};
    if (!formData.first_name.trim()) {
      newFieldErrors["first_name"] = true;
    }
    if (!formData.password.trim()) {
      newFieldErrors["password"] = true;
    }
    if (!formData.mobile_number.trim()) {
      newFieldErrors["mobile_number"] = true;
    }
    if (!formData.email.trim()) {
      newFieldErrors["email"] = true;
    }
    if (!formData.confirm_password.trim()) {
      newFieldErrors["confirm_password"] = true;
    }
    if (formData.password !== formData.confirm_password) {
      newFieldErrors["confirm_password"] = "Passwords do not match";
    }

    // If any field is missing, update error flags and do not proceed.
    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      return;
    }

    // Clear error flags and proceed to the next step.
    setFieldErrors({});
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  // Submit handler builds a FormData payload.
  const handleSubmit = async (e) => {
  e.preventDefault();

  setFieldErrors({});
  setApiErrors({});

  // Validate company fields
  const companyFieldErrors = {};

  if (!formData.company.company_name.trim())
    companyFieldErrors["company.company_name"] = "Company name is required";

  if (!formData.company.company_mail.trim())
    companyFieldErrors["company.company_mail"] = "Company email is required";

  if (!formData.company.location.trim())
    companyFieldErrors["company.location"] = "Company location is required";

  if (!formData.company.logo)
    companyFieldErrors["company.logo"] = "Company logo is required";

  if (Object.keys(companyFieldErrors).length > 0) {
    setFieldErrors(companyFieldErrors);
    return;
  }

  if (!termsAccepted) {
    setApiErrors({ general: "You must accept the terms and conditions." });
    return;
  }

  setIsLoading(true);

  const formPayload = new FormData();
  formPayload.append("first_name", formData.first_name);
  formPayload.append("email", formData.email);
  formPayload.append("mobile_number", formData.mobile_number);
  formPayload.append("password", formData.password);
  formPayload.append("confirm_password", formData.confirm_password);

  formPayload.append("company.company_name", formData.company.company_name);
  formPayload.append("company.company_mail", formData.company.company_mail);
  formPayload.append("company.location", formData.company.location);

  if (formData.company.logo) {
    formPayload.append("company.logo", formData.company.logo);
  }

  try {
    const result = await SellerRegister(formPayload);
console.log("result", result);

    if (result.success) {
      localStorage.removeItem("sellerVerificationEmail");

      setFormData({
        first_name: "",
        email: "",
        mobile_number: "",
        password: "",
        confirm_password: "",
        company: { company_name: "", company_mail: "", location: "", logo: null }, // ✅ FIXED
      });

      setFileName("");
      toast.success("Registration successful! Please log in.");
      // navigate("/seller/login/email");
      
    } else {
      const errors =
        typeof result.message === "object"
          ? result.message
          : { general: result.message || "Registration failed. Please try again." };
toast.error("Registration failed. Please try again.");
      setApiErrors(errors);
    }
  } catch (err) {
    console.error("Unexpected error:", err);
    setApiErrors({ general: "Something went wrong. Please try again." });
  } finally {
    setIsLoading(false);
  }
};

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   // Clear previous errors
  //   setFieldErrors({});
  //   setApiErrors({});

  //   // Validate company fields
  //   const companyFieldErrors = {};
  //   if (!formData.company.company_name.trim()) {
  //     companyFieldErrors["company.company_name"] = true;
  //   }
  //   if (!formData.company.company_mail.trim()) {
  //     companyFieldErrors["company.company_mail"] = true;
  //   }
  //   if (!formData.company.location.trim()) {
  //     companyFieldErrors["company.location"] = true;
  //   }
  //   if (!formData.company.logo) {
  //     companyFieldErrors["company.logo"] = true;
  //   }

  //   if (Object.keys(companyFieldErrors).length > 0) {
  //     setFieldErrors(companyFieldErrors);
  //     return;
  //   }

  //   // Ensure terms are accepted
  //   if (!termsAccepted) {
  //     setApiErrors({ general: "You must accept the terms and conditions." });
  //     return;
  //   }

  //   setIsLoading(true);

  //   // Build FormData payload
  //   const formPayload = new FormData();
  //   formPayload.append("first_name", formData.first_name);
  //   formPayload.append("email", formData.email);
  //   formPayload.append("mobile_number", formData.mobile_number);
  //   formPayload.append("password", formData.password);
  //   formPayload.append("confirm_password", formData.confirm_password);
  //   formPayload.append("company.company_name", formData.company.company_name);  // Changed
  //   formPayload.append("company.company_mail", formData.company.company_mail); // Changed
  //   formPayload.append("company.location", formData.company.location);
  //   if (formData.company.logo) {
  //     formPayload.append("company.logo", formData.company.logo);
  //   }
  //   try {
  //     const result = await SellerRegister(formPayload);
  //     console.log("registeration result", result);

  //     if (result.success) {
  //       // Registration successful - clear local storage
  //       localStorage.removeItem('sellerVerificationEmail');

  //       setFormData({
  //         first_name: "",
  //         email: "",
  //         mobile_number: "",
  //         password: "",
  //         confirm_password: "",
  //         company: { name: "", mail_id: "", location: "", logo: null },
  //       });
  //       setFileName("");
  //       navigate("/seller/login/email");
  //     } else {
  //       // Check if the error is returned as an object (field-specific errors)
  //       const errors =
  //         typeof result.message === "object"
  //           ? result.message
  //           : { general: result.message || "Registration failed. Please try again." };

  //       setApiErrors(errors);

  //     }
  //   } catch (err) {
  //     console.error("Unexpected error:", err);
  //     setApiErrors({ general: "Something went wrong. Please try again." });
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  return (
    <div className="grid h-screen w-screen grid-cols-1 md:grid-cols-2">
      <AuthBackgroundSeller />
      <div className="flex flex-col items-center justify-center p-4 md:p-4 md:h-full">
        <div className="flex w-full sm:w-[450px] flex-col items-center border-0 md:border-2 border-solid px-5 md:p-10 rounded-xl">
          <p className="text-start font-semibold text-3xl md:text-2xl">
            {currentStep === 1 ? "Personal Information" : "Company Information"}
          </p>
          <hr className="border-1 my-6 w-full border-solid border-graylight" />

          {currentStep === 1 && (
            <div className="w-full">
              <CommonInput
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="First Name"
                required
                error={fieldErrors["first_name"]}
              />

              <CommonInput
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                required
                disabled={!!verifiedEmail}
                error={apiErrors.email?.[0] || fieldErrors.email}
              />
              <CommonInput
                name="mobile_number"
                type="text"
                value={formData.mobile_number}
                onChange={handleChange}
                placeholder="Mobile Number"
                required
                error={apiErrors.mobile_number?.[0] || fieldErrors["mobile_number"]}
              />

              <div className="relative mb-4">
                <CommonInput
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Password"
                  required
                  error={fieldErrors.password}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              <div className="relative mb-4">
                <CommonInput
                  name="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirm_password}
                  onChange={handleChange}
                  placeholder="Confirm Password"
                  required
                  error={fieldErrors.confirm_password}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-2"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              <button
                onClick={handleNextStep}
                className="w-full rounded-xl px-6 font-semibold py-2 text-black bg-[#FFAE00]"
              >
                Next
              </button>
            </div>
          )}

          {currentStep === 2 && (
            <form className="w-full" onSubmit={handleSubmit}>
              <CommonInput
                name="company.company_name"  // Changed from company.name
                value={formData.company.company_name}
                onChange={handleChange}
                placeholder="Company Name"
                required
                error={fieldErrors["company.company_name"]}
              />

              <CommonInput
                name="company.company_mail"  // Changed from company.mail_id
                type="email"
                value={formData.company.company_mail}
                onChange={handleChange}
                placeholder="Company Email"
                required
                error={fieldErrors["company.company_mail"]}
              />

              <CommonInput
                name="company.location"
                value={formData.company.location}
                onChange={handleChange}
                placeholder="Company Location"
                required
                error={fieldErrors["company.location"]}
              />

              {/* Custom file upload UI for company logo */}
              <div className="mb-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    name="company.logo"
                    onChange={handleFileUpload}
                    className="hidden"
                    accept="image/*"
                  />
                  <div
                    className={`flex rounded px-3 py-2 w-full items-center justify-between 
                      ${fieldErrors["company.logo"] ? "border border-red-500" : "border border-green-600"}`}
                  >
                    {fileName ? (
                      <span>{fileName}</span>
                    ) : (
                      <span className="text-gray-400">Company Logo</span>
                    )}
                    <span className="flex items-center gap-2 text-xs text-gray-400">
                      Upload File <FaCloudUploadAlt />
                    </span>
                  </div>
                </label>
                {fieldErrors["company.logo"] && (
                  <p className="text-red-500 text-sm mt-1">Company logo is required</p>
                )}
              </div>

              <div className="flex items-center mb-1">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={() => setTermsAccepted(!termsAccepted)}
                  className="mr-2"
                />
                <label className="text-sm">
                  I agree to the{" "}
                  <span
                    className="text-brand-primary cursor-pointer"
                    onClick={() => setIsTermsOpen(true)}
                  >
                    terms & policy
                  </span>
                </label>
              </div>
              {/* {apiErrors.general && (
  <p className="text-red-500 text-sm mb-3">{apiErrors.general}</p>
)} */}
              {apiErrors.general && (
                <p className="text-red-500 text-sm mb-3">{apiErrors.general}</p>
              )}


              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="w-full rounded-xl px-6 font-semibold py-2 bg-white border border-gray-300 text-gray-500"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="w-full rounded-xl px-6 font-semibold py-2 bg-[#FFAE00] text-black"
                  disabled={isLoading}
                >
                  {isLoading ? "Registering..." : "Sign Up"}
                </button>
              </div>
            </form>
          )}

          <div className="flex justify-start w-full">
            <p className="text-center text-sm my-2">
              Have an account?{" "}
              <span className="cursor-pointer font-semibold text-[#FFAE00]">
                <Link to="/seller/login/email">Sign In</Link>
              </span>
            </p>
          </div>

          {/* Footer Logo */}
          <div className="mt-10 flex justify-center items-center w-full">
            <img src={img} alt="Bricks n Mix" className="h-8" />
          </div>
        </div>
      </div>
      <SellerTermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
    </div>
  );
}

export default SellerRegistration;